import { z } from 'zod';
import { EntityIdSchema, IsoDateTimeSchema, IdentifierSchema, PaginationSchema, RepositoryRelativePathSchema, } from './common.js';
import { FlowPipelineIdSchema, ProjectRepositoriesSchema, ProjectRepositorySchema, ProjectSchema, TestDeployModeSchema, WorkflowDecisionPolicySchema, WorkItemStatusSchema, } from './domain.js';
import { WorkItemKindSchema } from './workflow.js';
export const UpdateProjectInputSchema = z
    .object({
    key: ProjectSchema.shape.key.optional(),
    name: ProjectSchema.shape.name.optional(),
    repositories: ProjectRepositoriesSchema.optional(),
    repositoryUrl: ProjectRepositorySchema.shape.repositoryUrl.optional(),
    repositoryOwner: ProjectRepositorySchema.shape.repositoryOwner.optional(),
    repositoryName: ProjectRepositorySchema.shape.repositoryName.optional(),
    defaultBranch: ProjectRepositorySchema.shape.defaultBranch.optional(),
    testCommand: ProjectRepositorySchema.shape.testCommand.optional(),
    testDeployMode: TestDeployModeSchema.optional(),
    testDeployWebhookUrl: ProjectSchema.shape.testDeployWebhookUrl.optional(),
    testDeployFlowPipelineId: FlowPipelineIdSchema.nullable().optional(),
})
    .strict()
    .refine((input) => Object.keys(input).length > 0, { message: 'at least one field is required' })
    .refine((input) => !(input.repositories &&
    (input.repositoryUrl ||
        input.repositoryOwner ||
        input.repositoryName ||
        input.defaultBranch ||
        input.testCommand)), { message: 'repositories cannot be combined with legacy repository fields' });
export const ProjectListQuerySchema = PaginationSchema;
export const WorkflowSkillSummarySchema = z
    .object({
    key: IdentifierSchema,
    name: z.string().min(1).max(200),
    description: z.string().max(1_000),
})
    .strict();
export const WorkflowSkillUsageSchema = z
    .object({
    workflowKey: IdentifierSchema,
    workflowName: z.string().min(1).max(200),
    stageKey: IdentifierSchema,
    stageName: z.string().min(1).max(200),
})
    .strict();
export const WorkflowSkillCatalogItemSchema = WorkflowSkillSummarySchema.extend({
    usageCount: z.number().int().nonnegative(),
    usedBy: z.array(WorkflowSkillUsageSchema),
}).strict();
export const CreateWorkflowSkillInputSchema = z
    .object({
    key: IdentifierSchema,
    content: z.string().min(1).max(200_000),
})
    .strict();
export const WorkflowSkillSchema = WorkflowSkillSummarySchema.extend({
    content: z.string().min(1).max(200_000),
}).strict();
export const UpdateWorkflowSkillInputSchema = z
    .object({ content: WorkflowSkillSchema.shape.content })
    .strict();
export const CreateApiWorkItemInputSchema = z
    .object({
    projectId: EntityIdSchema.optional(),
    kind: WorkItemKindSchema,
    title: z.string().trim().min(1).max(200),
    description: z.string().trim().min(1).max(20_000),
    requestedBy: z.string().min(1).max(200).default('web'),
    workflowKey: z.string().min(1).max(64).optional(),
})
    .strict();
export const WorkItemListQuerySchema = PaginationSchema.extend({
    projectId: EntityIdSchema.optional(),
    status: WorkItemStatusSchema.optional(),
    scope: z.enum(['mine', 'all']).default('mine'),
}).strict();
export const WorkItemDetailQuerySchema = z
    .object({
    includeEvents: z
        .enum(['true', 'false'])
        .default('true')
        .transform((value) => value === 'true'),
})
    .strict();
export const WebAuthUserSchema = z
    .object({
    openId: z.string().min(1).max(200),
    name: z.string().min(1).max(200),
    avatarUrl: z.string().url().max(2_048).optional(),
})
    .strict();
export const WebAuthRoleSchema = z.enum(['admin', 'user']);
export const WebAuthFailureCodeSchema = z.enum([
    'invalid_state',
    'tenant_not_allowed',
    'user_not_allowed',
    'provider_error',
]);
export const WebAuthSessionSchema = z.union([
    z
        .object({
        authenticated: z.literal(false),
        provider: z.literal('feishu'),
        user: z.null(),
    })
        .strict(),
    z
        .object({
        authenticated: z.literal(true),
        provider: z.literal('feishu'),
        role: WebAuthRoleSchema,
        user: WebAuthUserSchema,
    })
        .strict(),
    z
        .object({
        authenticated: z.literal(true),
        provider: z.literal('local'),
        role: z.literal('admin'),
        user: z.null(),
    })
        .strict(),
]);
export const UserPetSchema = z
    .object({
    id: IdentifierSchema,
    displayName: z.string().trim().min(1).max(100),
    description: z.string().trim().max(500),
    spritesheetUrl: z
        .string()
        .regex(/^\/api\/v1\/me\/pet\/spritesheet\?revision=[a-f0-9]{64}$/, 'must be a revisioned same-origin Pet spritesheet URL'),
    updatedAt: IsoDateTimeSchema,
})
    .strict();
export const UserPetResponseSchema = z.object({ pet: UserPetSchema.nullable() }).strict();
/** Personal tokens let other agents call TCode as the Feishu user who issued them. */
export const ApiTokenSchema = z
    .object({
    id: EntityIdSchema,
    name: z.string().trim().min(1).max(100),
    prefix: z.string().min(1).max(32),
    createdAt: IsoDateTimeSchema,
    expiresAt: IsoDateTimeSchema,
    lastUsedAt: IsoDateTimeSchema.nullable(),
})
    .strict();
export const CreateApiTokenInputSchema = z
    .object({
    name: z.string().trim().min(1).max(100),
    expiresInDays: z.union([z.literal(30), z.literal(90), z.literal(180)]).default(90),
})
    .strict();
export const CreatedApiTokenSchema = z
    .object({ apiToken: ApiTokenSchema, token: z.string().min(1) })
    .strict();
export const ApiTokenListSchema = z.object({ data: z.array(ApiTokenSchema) }).strict();
export const CurrentUserSchema = z
    .object({
    user: WebAuthUserSchema,
    role: WebAuthRoleSchema,
    authentication: z.enum(['admin-token', 'api-token', 'web-session']),
})
    .strict();
export const UpdateWorkItemDecisionPolicyInputSchema = z
    .object({ policy: WorkflowDecisionPolicySchema })
    .strict();
export const CreateWorkItemRecoveryInputSchema = z
    .object({
    workflowRunId: EntityIdSchema,
    failedStageRunId: EntityIdSchema,
    action: z.literal('FIX_AND_RESUME'),
    instruction: z.string().trim().min(1).max(20_000),
    idempotencyKey: z
        .string()
        .min(1)
        .max(200)
        .regex(/^[A-Za-z0-9._:-]+$/),
})
    .strict();
/** A tester's verdict on a delivered run, given on the web instead of the Feishu card. */
export const CreateDeliveryFeedbackInputSchema = z
    .object({
    workflowRunId: EntityIdSchema,
    result: z.enum(['PASSED', 'ISSUE']),
    comment: z.string().trim().max(20_000).optional(),
    idempotencyKey: z
        .string()
        .min(1)
        .max(200)
        .regex(/^[A-Za-z0-9._:-]+$/),
})
    .strict()
    .refine((input) => input.result === 'PASSED' || Boolean(input.comment), {
    message: 'Describe the issue found in testing',
    path: ['comment'],
});
/**
 * Continues a delivered or failed Work Item with a workflow that Feishu users can also ask the
 * assistant for (`metadata.assistantTrigger`), such as `resolve-conflict`.
 */
export const CreateWorkItemAssistantWorkflowInputSchema = z
    .object({
    workflowKey: z.string().trim().min(1).max(200),
    workflowRunId: EntityIdSchema,
    note: z.string().trim().max(20_000).optional(),
    idempotencyKey: z
        .string()
        .min(1)
        .max(200)
        .regex(/^[A-Za-z0-9._:-]+$/),
})
    .strict();
export const ConversationChannelSchema = z.enum(['FEISHU', 'WEB']);
/**
 * What a delivery asks the tester to judge: a code change to test, or a conclusion that no
 * product change was needed (with the reason), matching the Feishu status card's wording.
 */
export const DeliveryConclusionSchema = z.enum([
    'TEST',
    'NO_PRODUCT_CHANGE',
    'NOT_APPLICABLE',
    'SATISFIED',
]);
/**
 * One exchange about a Work Item, from Feishu or the web: a test verdict, a request to keep
 * fixing, or a question with TCode's answer.
 */
export const WorkItemConversationEntrySchema = z
    .object({
    id: z.string().min(1),
    kind: z.enum(['DELIVERY_FEEDBACK', 'CONTINUATION', 'QUESTION']),
    channel: ConversationChannelSchema,
    /** Display name when TCode knows it; the web shows a generic label otherwise. */
    authorName: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
    text: z.string(),
    result: z.enum(['PASSED', 'ISSUE']).nullable(),
    /** For delivery feedback: what the judged delivery was. */
    conclusion: DeliveryConclusionSchema.nullable(),
    answer: z.string().nullable(),
    answerStatus: z.enum(['PENDING', 'RUNNING', 'COMPLETED', 'FAILED']).nullable(),
})
    .strict();
export const WorkItemConversationSchema = z
    .object({
    entries: z.array(WorkItemConversationEntrySchema),
    feedback: z
        .object({
        /** The delivered run waiting for a verdict, or null when none is. */
        workflowRunId: EntityIdSchema.nullable(),
        conclusion: DeliveryConclusionSchema.nullable(),
        canSubmit: z.boolean(),
        recorded: z
            .object({ result: z.enum(['PASSED', 'ISSUE']), channel: ConversationChannelSchema })
            .nullable(),
    })
        .strict(),
})
    .strict();
export const EventListQuerySchema = z
    .object({
    afterSequence: z.coerce.number().int().min(0).default(0),
    limit: z.coerce.number().int().min(1).max(1_000).default(250),
})
    .strict();
export const SandboxFileQuerySchema = z.object({ path: RepositoryRelativePathSchema }).strict();
export const WorkspaceSessionSchema = z
    .object({
    url: z
        .string()
        .url()
        .refine((value) => {
        const url = new URL(value);
        return (['http:', 'https:'].includes(url.protocol) &&
            !url.username &&
            !url.password &&
            /^\/workspaces\/bootstrap\/[A-Za-z0-9_-]{16,64}$/.test(url.pathname) &&
            !url.search &&
            !url.hash);
    }, 'workspace URL must be an isolated one-time bootstrap URL'),
    expiresAt: IsoDateTimeSchema,
})
    .strict();
export const BrowserSessionSchema = z
    .object({
    url: z
        .string()
        .url()
        .refine((value) => {
        const url = new URL(value);
        return (['http:', 'https:'].includes(url.protocol) &&
            !url.username &&
            !url.password &&
            /^\/browsers\/bootstrap\/[A-Za-z0-9_-]{16,64}$/.test(url.pathname) &&
            !url.search &&
            !url.hash);
    }, 'browser URL must be an isolated one-time bootstrap URL'),
    expiresAt: IsoDateTimeSchema,
})
    .strict();
export const IntegrationCheckSchema = z
    .object({
    key: z.string().min(1).max(64),
    status: z.enum(['PASS', 'FAIL', 'MANUAL']),
    message: z.string().min(1).max(500),
})
    .strict();
export const IntegrationReadinessSchema = z
    .object({
    key: z.string().min(1).max(64),
    status: z.enum(['READY', 'DEGRADED', 'DISABLED']),
    checkedAt: IsoDateTimeSchema.nullable(),
    requiredScopes: z.array(z.string().min(1).max(200)),
    missingScopes: z.array(z.string().min(1).max(200)),
    checks: z.array(IntegrationCheckSchema),
})
    .strict();
const TokenCountSchema = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const CodexTokenUsageSchema = z
    .object({
    inputTokens: TokenCountSchema,
    cachedInputTokens: TokenCountSchema,
    outputTokens: TokenCountSchema,
    totalTokens: TokenCountSchema,
})
    .strict()
    .superRefine((usage, context) => {
    if (usage.cachedInputTokens > usage.inputTokens) {
        context.addIssue({ code: 'custom', message: 'cached input must be a subset of input' });
    }
    if (usage.totalTokens !== usage.inputTokens + usage.outputTokens) {
        context.addIssue({ code: 'custom', message: 'total tokens must equal input plus output' });
    }
});
export const WorkItemSkillTokenUsageSchema = z
    .object({
    key: z.string().min(1).max(200),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/u),
    estimatedTokens: TokenCountSchema,
    estimator: z.literal('TCODE_HEURISTIC_V1'),
})
    .strict();
export const WorkItemTokenUsageNodeSchema = z
    .object({
    stageRunId: EntityIdSchema,
    workflowRunId: EntityIdSchema,
    workflowKey: z.string().min(1).max(64),
    workflowName: z.string().min(1).max(200),
    stageKey: z.string().min(1).max(64),
    stageName: z.string().min(1).max(200),
    attempt: z.number().int().min(1),
    usage: CodexTokenUsageSchema.nullable(),
    skills: z.array(WorkItemSkillTokenUsageSchema),
    recordedAt: IsoDateTimeSchema,
})
    .strict();
export const WorkItemTokenUsageSkillSummarySchema = z
    .object({
    key: z.string().min(1).max(200),
    invocationCount: z.number().int().nonnegative(),
    estimatedTokens: TokenCountSchema,
    nodes: z.array(z.object({ stageRunId: EntityIdSchema, stageName: z.string().min(1).max(200) }).strict()),
})
    .strict();
export const WorkItemTokenUsageSchema = z
    .object({
    workItemId: EntityIdSchema,
    totals: z
        .object({
        inputTokens: TokenCountSchema,
        cachedInputTokens: TokenCountSchema,
        outputTokens: TokenCountSchema,
        totalTokens: TokenCountSchema,
        invocationCount: z.number().int().nonnegative(),
        measuredInvocationCount: z.number().int().nonnegative(),
        missingInvocationCount: z.number().int().nonnegative(),
    })
        .strict()
        .superRefine((totals, context) => {
        if (totals.cachedInputTokens > totals.inputTokens) {
            context.addIssue({ code: 'custom', message: 'cached input must be a subset of input' });
        }
        if (totals.totalTokens !== totals.inputTokens + totals.outputTokens) {
            context.addIssue({
                code: 'custom',
                message: 'total tokens must equal input plus output',
            });
        }
        if (totals.invocationCount !==
            totals.measuredInvocationCount + totals.missingInvocationCount) {
            context.addIssue({ code: 'custom', message: 'invocation counts must reconcile' });
        }
    }),
    nodes: z.array(WorkItemTokenUsageNodeSchema),
    skills: z.array(WorkItemTokenUsageSkillSummarySchema),
})
    .strict()
    .superRefine((report, context) => {
    if (report.totals.invocationCount !== report.nodes.length) {
        context.addIssue({ code: 'custom', message: 'invocation count must equal node records' });
    }
    const measuredInvocationCount = report.nodes.filter((node) => node.usage !== null).length;
    if (report.totals.measuredInvocationCount !== measuredInvocationCount) {
        context.addIssue({ code: 'custom', message: 'measured count must equal measured nodes' });
    }
    for (const skill of report.skills) {
        if (skill.invocationCount !== skill.nodes.length) {
            context.addIssue({
                code: 'custom',
                message: 'Skill invocation count must equal its nodes',
            });
        }
    }
});
//# sourceMappingURL=api.js.map